import React, { useEffect, useRef } from 'react';
import { useTutorial } from '../../../contexts/TutorialContext';

interface TutorialAutoStartProps {
  pageKey: string;
  delay?: number;  // Delay in ms before showing tutorial
}

/**
 * Component that automatically starts a tutorial on first visit to a page.
 * Place this component on any portal page that should show a tutorial.
 */
export const TutorialAutoStart: React.FC<TutorialAutoStartProps> = ({
  pageKey,
  delay = 500,  // Default 500ms delay to let page render
}) => {
  const {
    shouldShowTutorial,
    startTutorial,
    setCurrentPage,
    loading,
    isActive,
  } = useTutorial();

  const hasStarted = useRef(false);

  // Set current page when component mounts
  useEffect(() => {
    setCurrentPage(pageKey);
  }, [pageKey, setCurrentPage]);

  // Auto-start tutorial if it should be shown
  useEffect(() => {
    // Don't start if still loading, already active, or already started
    if (loading || isActive || hasStarted.current) {
      return;
    }

    // Check if we should show this tutorial
    if (!shouldShowTutorial(pageKey)) {
      return;
    }

    // Start after delay to let page render
    const timer = setTimeout(() => {
      if (!hasStarted.current && shouldShowTutorial(pageKey)) {
        hasStarted.current = true;
        startTutorial(pageKey);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [loading, isActive, pageKey, shouldShowTutorial, startTutorial, delay]);

  // Reset hasStarted when page changes
  useEffect(() => {
    hasStarted.current = false;
  }, [pageKey]);

  // This component doesn't render anything
  return null;
};

export default TutorialAutoStart;
